import { Box } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import type { FC } from "react";
import { HALLOWEEN } from "@/theme/tokens";
import { PUMPKIN_BODY_PATH, PUMPKIN_STEM_PATH, PUMPKIN_VIEW_HEIGHT, PUMPKIN_VIEW_WIDTH } from "./halloweenShapes";

/** Matches the success checkmark it stands in for, so the dialog keeps its layout. */
const DISC_SIZE_PX = 120;
const PUMPKIN_WIDTH_PX = 76;
const POP_SPRING = { type: "spring", stiffness: 420, damping: 14 } as const;
const WOBBLE_DEGREES = [0, -10, 8, -4, 0];
const WOBBLE_SECONDS = 0.7;
const WOBBLE_DELAY_SECONDS = 0.35;

/** A grinning jack-o'-lantern that pops in on a soft orange disc and wobbles once. Plays once; still under reduced motion. */
export const PumpkinPop: FC<{ dataTestId?: string }> = ({ dataTestId }) => {
	const reduceMotion = useReducedMotion();
	return (
		<Box
			data-testid={dataTestId}
			aria-hidden
			sx={{ width: DISC_SIZE_PX, height: DISC_SIZE_PX, borderRadius: "50%", bgcolor: HALLOWEEN.PUMPKIN_TINT, display: "grid", placeItems: "center" }}
		>
			<motion.div
				initial={reduceMotion ? false : { scale: 0.3, opacity: 0 }}
				animate={reduceMotion ? undefined : { scale: 1, opacity: 1, rotate: WOBBLE_DEGREES }}
				transition={{
					scale: POP_SPRING,
					opacity: { duration: 0.15 },
					rotate: { duration: WOBBLE_SECONDS, delay: WOBBLE_DELAY_SECONDS, ease: "easeInOut" },
				}}
				style={{ lineHeight: 0 }}
			>
				<svg
					aria-hidden="true"
					width={PUMPKIN_WIDTH_PX}
					height={(PUMPKIN_WIDTH_PX * PUMPKIN_VIEW_HEIGHT) / PUMPKIN_VIEW_WIDTH}
					viewBox={`0 0 ${PUMPKIN_VIEW_WIDTH} ${PUMPKIN_VIEW_HEIGHT}`}
				>
					<path d={PUMPKIN_STEM_PATH} fill="none" stroke={HALLOWEEN.STEM} strokeWidth={5} strokeLinecap="round" />
					<path d="M50 6 C58 0 66 3 67 8 C61 11 55 10 50 6Z" fill={HALLOWEEN.LEAF} />
					<path d={PUMPKIN_BODY_PATH} fill={HALLOWEEN.PUMPKIN} />
					<path d="M42 20 C36 34 36 56 42 68 M42 20 C48 34 48 56 42 68" fill="none" stroke={HALLOWEEN.PUMPKIN_DARK} strokeOpacity={0.35} strokeWidth={2} />
					<path
						d="M26 36 L33 30 L37 39 Z M58 36 L51 30 L47 39 Z M24 50 Q42 62 60 50 L55 52 L52 48 L47 54 L42 49 L37 54 L32 48 L29 52 Z"
						fill={HALLOWEEN.FACE}
					/>
				</svg>
			</motion.div>
		</Box>
	);
};
